import { useState } from 'react';
import { Link } from 'react-router-dom';
import { App, Button, Checkbox, DatePicker, Input, List, Popconfirm, Select, Space, Switch, Tag, Typography } from 'antd';
import { DeleteOutlined } from '@ant-design/icons';
import dayjs, { type Dayjs } from 'dayjs';
import type { TaskItem } from '@crm/shared';
import { useCreateTask, useDeleteTask, useTasks, useUpdateTask } from '../features/tasks/api';
import { useUsers } from '../features/users/api';
import { useFeatures } from '../features/settings/api';
import { errorMessage } from '../api/client';
import { EmptyState, LoadError } from './states';
import { fmtDateTime } from '../utils/format';

interface Props {
  contactId?: string;
  organizationId?: string;
  dealId?: string;
}

export function TaskList({ contactId, organizationId, dealId }: Props) {
  const { message } = App.useApp();
  const [showDone, setShowDone] = useState(false);
  const [title, setTitle] = useState('');
  const [due, setDue] = useState<Dayjs | null>(null);
  const [assignee, setAssignee] = useState<string | undefined>(undefined);
  const tasks = useTasks({ contactId, organizationId, dealId, status: showDone ? 'all' : 'open', pageSize: 100 });
  const users = useUsers();
  const create = useCreateTask();
  const update = useUpdateTask();
  const remove = useDeleteTask();
  const tasksOn = useFeatures().enabled('tasks');

  const add = async () => {
    if (!title.trim()) return;
    try {
      await create.mutateAsync({ title: title.trim(), dueAt: due ? due.toISOString() : null, contactId, organizationId, dealId, assigneeUserId: assignee });
      setTitle('');
      setDue(null);
    } catch (e) {
      message.error(errorMessage(e));
    }
  };

  const dueTag = (t: TaskItem) => {
    if (!t.dueAt) return null;
    const d = dayjs(t.dueAt);
    const overdue = !t.completedAt && d.isBefore(dayjs());
    return <Tag color={overdue ? 'error' : d.isBefore(dayjs().add(1, 'day')) ? 'warning' : 'default'}>{overdue ? 'Overdue · ' : 'Due '}{fmtDateTime(t.dueAt)}</Tag>;
  };

  return (
    <div>
      <Space.Compact style={{ width: '100%', marginBottom: 12 }}>
        <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="New task…" aria-label="New task title" onPressEnter={() => void add()} />
        <DatePicker showTime={{ format: 'HH:mm' }} value={due} onChange={setDue} placeholder="Due" aria-label="Due date" />
        <Select
          allowClear
          placeholder="Assignee"
          style={{ minWidth: 150 }}
          value={assignee}
          onChange={setAssignee}
          options={(users.data ?? []).filter((u) => u.status !== 'DISABLED').map((u) => ({ value: u.id, label: u.status === 'INVITED' ? `${u.name} (invited)` : u.name }))}
        />
        <Button type="primary" onClick={() => void add()} loading={create.isPending} disabled={!title.trim()}>
          Add
        </Button>
      </Space.Compact>
      <div style={{ marginBottom: 8 }}>
        <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
          <Switch size="small" checked={showDone} onChange={setShowDone} aria-label="Show completed tasks" />
          <Typography.Text type="secondary">Show completed</Typography.Text>
        </label>
      </div>
      {tasks.isError ? (
        <LoadError error={tasks.error} onRetry={tasks.refetch} title="Could not load tasks" />
      ) : tasks.data && tasks.data.items.length === 0 ? (
        <EmptyState description={showDone ? 'No tasks yet.' : 'No open tasks. Add one above.'} />
      ) : (
        <List
          size="small"
          loading={tasks.isLoading}
          dataSource={tasks.data?.items ?? []}
          renderItem={(t) => (
            <List.Item
              actions={[
                <Popconfirm
                  key="del"
                  title="Delete task?"
                  okText="Delete"
                  okButtonProps={{ danger: true, loading: remove.isPending }}
                  onConfirm={() => remove.mutateAsync(t.id).catch((e) => message.error(errorMessage(e)))}
                >
                  <Button size="small" type="text" icon={<DeleteOutlined />} aria-label="Delete task" title="Delete task" />
                </Popconfirm>,
              ]}
            >
              <Space align="start">
                <Checkbox
                  checked={!!t.completedAt}
                  aria-label={t.completedAt ? `Mark "${t.title}" as open` : `Mark "${t.title}" as done`}
                  onChange={(e) => update.mutate({ id: t.id, completed: e.target.checked }, { onError: (err) => message.error(errorMessage(err)) })}
                />
                <div>
                  <Typography.Text delete={!!t.completedAt}>{tasksOn ? <Link to={`/tasks/${t.id}`}>{t.title}</Link> : t.title}</Typography.Text>
                  <div>
                    {dueTag(t)}
                    {t.assignee && <Typography.Text type="secondary" style={{ fontSize: 12 }}>{t.assignee.name}</Typography.Text>}
                  </div>
                </div>
              </Space>
            </List.Item>
          )}
        />
      )}
    </div>
  );
}
